import { useState, useContext, createContext } from 'react';
import { updateElementsTheme } from '../utils/themeUtils';

// Определение тем
export const themes = {
    light: {
        name: 'light',
        background: '#f5f7fa',
        canvasBackground: '#ffffff',
        elements: {
            rectangle: {
                fill: '#ffffff',
                stroke: '#2d3748',
                strokeWidth: 2
            },
            circle: {
                fill: '#ffffff',
                stroke: '#2d3748',
                strokeWidth: 2
            },
            arrow: {
                stroke: '#2d3748',
                strokeWidth: 2
            },
            text: {
                fill: '#2d3748'
            },
            note: {
                fill: '#fffbe6',
                stroke: '#d4b106',
                strokeWidth: 2
            },
            database: {
                fill: '#f0f8ff',
                stroke: '#4a90e2',
                strokeWidth: 2
            }
        },
        toolbar: {
            background: 'linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)',
            border: '#e1e8ed',
            shadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
            text: '#1a202c',
            buttonBackground: 'transparent',
            buttonBackgroundActive: '#e2e8f0',
            buttonBorder: '#cbd5e0',
            buttonText: '#4a5568'
        },
        codePanel: {
            background: '#ffffff',
            border: '#e1e8ed',
            text: '#1a202c',
            shadow: '0 4px 12px rgba(0, 0, 0, 0.1)'
        },
        contextMenu: {
            background: '#ffffff',
            border: '#e1e8ed',
            text: '#1a202c',
            shadow: '0 4px 12px rgba(0, 0, 0, 0.15)'
        }
    },
    dark: {
        name: 'dark',
        background: '#0f1419',
        canvasBackground: '#1e2328',
        elements: {
            rectangle: {
                fill: '#2a2d31',
                stroke: '#e2e8f0',
                strokeWidth: 2
            },
            circle: {
                fill: '#2a2d31',
                stroke: '#e2e8f0',
                strokeWidth: 2
            },
            arrow: {
                stroke: '#e2e8f0',
                strokeWidth: 2
            },
            text: {
                fill: '#e2e8f0'
            },
            note: {
                fill: '#3a3226',
                stroke: '#d4b106',
                strokeWidth: 2
            },
            database: {
                fill: '#1e2a3a',
                stroke: '#4a90e2',
                strokeWidth: 2
            }
        },
        toolbar: {
            background: 'linear-gradient(135deg, #1f2124 0%, #2b2e33 100%)',
            border: '#455a64',
            shadow: '0 2px 8px rgba(0, 0, 0, 0.3)',
            text: '#ffffff',
            buttonBackground: 'transparent',
            buttonBackgroundActive: '#2b2e33',
            buttonBorder: '#455a64',
            buttonText: '#ffffff'
        },
        codePanel: {
            background: '#1f2124',
            border: '#2b2e33',
            text: '#ffffff',
            shadow: '0 4px 12px rgba(0, 0, 0, 0.4)'
        },
        contextMenu: {
            background: '#1f2124',
            border: '#2b2e33',
            text: '#ffffff',
            shadow: '0 4px 12px rgba(0, 0, 0, 0.4)'
        }
    }
};

// Создаем контекст темы
const ThemeContext = createContext();

// Хук для использования темы
export const useTheme = () => {
    const context = useContext(ThemeContext);
    if (!context) {
        throw new Error('useTheme must be used within a ThemeProvider');
    }
    return context;
};

// Провайдер темы
export const ThemeProvider = ({ children }) => {
    const [currentTheme, setCurrentTheme] = useState(() => {
        // Попытка загрузить тему из localStorage
        const savedTheme = localStorage.getItem('arch-editor-theme');
        return savedTheme && themes[savedTheme] ? savedTheme : 'dark';
    });

    // Callback для обновления цветов элементов при смене темы
    const [onElementsUpdate, setOnElementsUpdate] = useState(null);

    const toggleTheme = () => {
        const newTheme = currentTheme === 'light' ? 'dark' : 'light';
        setCurrentTheme(newTheme);
        localStorage.setItem('arch-editor-theme', newTheme);

        // Обновляем цвета элементов при смене темы
        if (onElementsUpdate) {
            onElementsUpdate((prevElements) =>
                updateElementsTheme(prevElements, themes[newTheme])
            );
        }
    };

    const setTheme = (themeName) => {
        if (themes[themeName]) {
            setCurrentTheme(themeName);
            localStorage.setItem('arch-editor-theme', themeName);

            // Обновляем цвета элементов при смене темы
            if (onElementsUpdate) {
                onElementsUpdate((prevElements) =>
                    updateElementsTheme(prevElements, themes[themeName])
                );
            }
        }
    };

    // Функция для регистрации callback обновления элементов
    const registerElementsUpdate = (callback) => {
        setOnElementsUpdate(() => callback);
    };

    const theme = themes[currentTheme];

    return (
        <ThemeContext.Provider value={{
            theme,
            currentTheme,
            toggleTheme,
            setTheme,
            themes,
            registerElementsUpdate,
            updateElementsTheme: (elements) => updateElementsTheme(elements, theme)
        }}>
            {children}
        </ThemeContext.Provider>
    );
};
